<template>
    <div v-auto-animate>
        <h2
            class="text-lg font-medium leading-7 text-iron-700 tracking-[-0.02em]"
        >
            {{ $t("event_types.event_detail.availability") }}
        </h2>

        <p
            v-if="!eventTypeUser.use_calendar_integration_availability"
            class="text-sm text-iron-500 tracking-[-0.01em]"
        >
            {{ $t("event_types.availability.description") }}
        </p>

        <div>
            <p
                v-if="eventTypeUser.use_calendar_integration_availability"
                class="text-sm text-iron-500 tracking-[-0.01em]"
            >
                {{
                    $t("event_types.availability.description_calendar_managed")
                }}
            </p>
        </div>

        <div class="mt-6 pb-4 flex flex-col border-b">
            <div class="gap-2 text-sm font-medium flex items-center">
                <Switch
                    id="availabilitySwitch"
                    :checked="eventTypeUser.use_availability"
                    @update:checked="toggleUseAvailability"
                />
                <Label for="availabilitySwitch">{{
                    $t("event_types.availability.repeating_availability")
                }}</Label>
            </div>
            <div class="mt-4 gap-2 text-sm font-medium flex items-center">
                <Switch
                    :disabled="isICloudCalendar"
                    id="calendarManagedAvailability"
                    :checked="
                        eventTypeUser.use_calendar_integration_availability
                    "
                    @update:checked="toggleUseCalendarIntegrationAvailability"
                />
                <div>
                    <div class="gap-2 flex">
                        <Label for="calendarManagedAvailability">{{
                            $t(
                                "event_types.availability.calendar_managed_availability",
                            )
                        }}</Label>

                        <PaidFeatureIcon v-if="!hasSubscription" />

                        <a
                            target="_blank"
                            href="https://napoveda.zaptime.cz/dostupnost.html#co-znamena-kalendarem-spravovana-dostupnost"
                        >
                            <BadgeHelp
                                class="text-iron-500"
                                :size="20"
                                :stroke-width="2.25"
                            />
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="eventTypeUser.use_availability">
            <p class="mt-8 text-sm font-medium text-iron-700">
                {{
                    $t(
                        "event_types.availability.choose_existing_availabilities",
                    )
                }}
            </p>

            <div class="mt-2 gap-2 flex">
                <Select
                    :model-value="String(eventTypeUser.availability_id)"
                    @update:model-value="assignAvailability"
                >
                    <SelectTrigger>
                        <SelectValue
                            :placeholder="
                                $t('event_types.availability.choose_scheme')
                            "
                        />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectGroup>
                            <SelectItem
                                v-for="availability of availabilities"
                                :key="availability.id"
                                :value="String(availability.id)"
                            >
                                {{ availability.name }}
                            </SelectItem>
                        </SelectGroup>
                    </SelectContent>
                </Select>

                <div class="shrink-0">
                    <CreateAvailabilityModal @on-finish="onAvailabilityAdded">
                        <Button variant="outline">
                            <Plus class="mr-2" :size="16"></Plus>
                            {{ $t("availability.new_availability") }}
                        </Button>
                    </CreateAvailabilityModal>
                </div>
            </div>

            <p
                v-if="availabilityDay"
                class="mt-4 text-sm font-medium text-iron-700"
            >
                {{ $t("event_types.availability.available") }}
            </p>

            <div class="mt-2" v-if="availabilityDay">
                <div class="rounded-lg border-iron-300 border">
                    <div
                        v-for="(
                            availabityRanges, dayName, i
                        ) in availabilityDay"
                        :key="dayName"
                        class="border-iron-300 px-4 py-2"
                        :class="[i !== 0 ? 'border-t' : '']"
                    >
                        <p class="text-sm font-medium text-iron-500">
                            {{ dayName }}
                        </p>

                        <p
                            v-for="(range, i) of availabityRanges"
                            :key="i"
                            class="mt-1 text-iron-900 tracking-[-0.02rem]"
                        >
                            <span class="w-10 inline-block"
                                >{{ range.split("-")[0] }}
                            </span>
                            –
                            <span class="inline-block"
                                >{{ range.split("-")[1] }}
                            </span>
                        </p>
                    </div>
                </div>

                <SetMeetingDuration class="mt-4" :event-type="eventType" />
            </div>
        </div>
    </div>

    <CalendarManagedAvailabilityName
        :event-type="eventType"
        :event-type-user="eventTypeUser"
        v-if="eventTypeUser.use_calendar_integration_availability"
    />
</template>

<script lang="ts" setup>
import { EventType } from "../types/EventType";
import { EventTypeUser } from "../types/EventTypeUser";
import SetMeetingDuration from "./BookingPage/components/SetMeetingDuration.vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { Button } from "@/components/ui/button";
import CreateAvailabilityModal from "../../Availabilities/components/ManageAvailabilityModal.vue";
import { Plus } from "lucide-vue-next";
import { useCalendar } from "@zaptime/core";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { vAutoAnimate } from "@formkit/auto-animate";
import CalendarManagedAvailabilityName from "./Availability/CalendarManagedAvailabilityName.vue";

import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { ref } from "vue";
import { useHasSubscription } from "@/composables/useHasSubscription";
import useProDialog from "@/ProModal/composables/useProDialog";
import PaidFeatureIcon from "../../Atomic/PaidFeatureIcon.vue";
import { AvailabilityDay } from "../../Availabilities/types";
import { BadgeHelp } from "lucide-vue-next";

const props = defineProps<{
    eventType: EventType;
    eventTypeUser: EventTypeUser;
    availabilities: any[];
    availabilityDay?: AvailabilityDay;
}>();

const { hasSubscription } = useHasSubscription(props.eventType);
const proDialog = useProDialog();
const calendar = useCalendar();
const isICloudCalendar = ref(false);

function onAvailabilityAdded() {
    const lastAdded = props.availabilities[props.availabilities.length - 1];

    assignAvailability(lastAdded.id);
}

const assignAvailability = (availabilityId: string) => {
    if (props.eventType.availability_id == null) {
        router.post(
            route("app.event-types.availability-settings", props.eventType.id),
            {
                duration: 30,
                offset: 10,
            },
        );
    }

    router.post(
        "/event-types/" + props.eventType.id + "/availability",
        {
            availabilityId: availabilityId,
        },
        {
            onFinish: () => {
                refresh();
            },
        },
    );
};

function toggleUseCalendarIntegrationAvailability() {
    if (!hasSubscription) {
        proDialog.show({
            feature: "calendar_managed_availability",
        });
        return;
    }

    router.post(
        route(
            "app.event-types.use-calendar-integration-availability",
            props.eventType.id,
        ),
        {},
        {
            onFinish: () => {
                refresh();
            },
        },
    );
}

function toggleUseAvailability() {
    router.post(
        route("app.event-types.use-availability", props.eventType.id),
        {},
        {
            onFinish: async () => {
                refresh();
            },
        },
    );
}

async function refresh() {
    await calendar.getDays();
}
</script>
